/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */

import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import "../../styles/AdminEntities.css";

/**
 * Componente modal para creación y edición de personas/trabajadores.
 * 
 * Modal complejo que gestiona el formulario de personas incluyendo:
 * - Campos básicos de información personal
 * - Selección de departamento
 * - Subida y gestión de fotografías
 * - Integración con Microsoft Entra para autenticación
 * 
 * Soporta tanto creación (POST) como edición (PUT) de personas.
 */

const ModalPersona = ({ show, onClose, onSave, departamentos, edificios, persona }) => {

  /* ============================
      Estado del formulario
     ============================ */
  // Campos básicos de la persona
  const [nombre, setNombre] = useState("");
  const [apellido1, setApellido1] = useState("");
  const [apellido2, setApellido2] = useState("");
  const [cargo, setCargo] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [extension, setExtension] = useState("");
  const [departamentoId, setDepartamentoId] = useState("");
  const [edificioId, setEdificioId] = useState("");

  // Gestión de fotografía
  const [foto, setFoto] = useState(null);
  const [fotoActual, setFotoActual] = useState(null);
  const [confirmDeleteFotoModal, setConfirmDeleteFotoModal] = useState(false);
  const [isFotoDeleted, setIsFotoDeleted] = useState(false);

  // Referencia al input de archivo para resetearlo
  const fileInputRef = useRef(null);

  /* ============================
     Autenticación (MSAL)
     ============================ */
  const { instance, accounts } = useMsal();

  /**
   * Obtiene token de acceso para autenticar peticiones API.
   * Reutiliza la misma lógica que otros componentes de administración.
   */
  const getAccessToken = async () => {
    if (!accounts || accounts.length === 0) return null;

    try {
      const resp = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [process.env.REACT_APP_API_SCOPE],
      });

      return resp.accessToken;
    } catch (err) {
      console.error("Error obteniendo token MSAL:", err);
      return null;
    }
  };

  /* ============================
     Efecto: inicializar formulario
     ============================ */
  
  /**
   * Inicializa los campos del formulario según modo (crear/editar).
   * Si es modo editar, carga sus datos; si es modo crear, limpia el formulario.
   * También gestiona el estado de la fotografía.
   */
  useEffect(() => {
    if (persona) {
      // Modo edición: cargar datos existentes
      setNombre(persona.nombre || "");
      setApellido1(persona.apellido1 || "");
      setApellido2(persona.apellido2 || "");
      setCargo(persona.cargo || "");
      setEmail(persona.email || "");
      setTelefono(persona.telefono || "");
      setExtension(persona.extension || "");
      setDepartamentoId(persona.departamento?.id || "");
      setEdificioId(persona.edificio?.id || "");
      setFoto(null);
      setFotoActual(persona.fotoUrl || null);
      setIsFotoDeleted(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } else {
      // Modo creación: limpiar formulario
      setNombre("");
      setApellido1("");
      setApellido2("");
      setCargo("");
      setEmail("");
      setTelefono("");
      setExtension("");
      setDepartamentoId("");
      setEdificioId("");
      setFoto(null);
      setFotoActual(null);
      setIsFotoDeleted(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [persona, show]);

  /* ============================
     Gestión de fotografía
     ============================ */
  
  /**
   * Maneja la eliminación definitiva de la foto.
   * Se ejecuta tras confirmación del usuario.
   */
  const handleDeleteFotoNueva = () => {
    setFotoActual(null);
    setIsFotoDeleted(true);
    setConfirmDeleteFotoModal(false);
  };

  /**
   * Abre el modal de confirmación para eliminar foto.
   */
  const handleConfirmDeleteFoto = () => {
    setConfirmDeleteFotoModal(true);
  };

  /* ============================
     Manejo de envío del formulario
     ============================ */
  
  /**
   * Maneja el envío del formulario (crear o editar).
   * Gestiona la subida de fotos y las peticiones a la API.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = await getAccessToken();
    if (!token) {
      alert("No se pudo obtener token MSAL");
      return;
    }

    try {
      const personaData = {
        // Datos básicos de la persona
        nombre,
        apellido1,
        apellido2,
        cargo: cargo || null,
        email,
        telefono: telefono || null,
        extension: extension || null,
        departamento: departamentoId ? { id: Number(departamentoId) } : null,
        edificio: edificioId ? { id: Number(edificioId) }: null,
      };

      if (persona) {
        // === MODO EDICIÓN (UPDATE) ===
        const formDataUpdate = new FormData();

        // Añadir datos de persona como JSON
        formDataUpdate.append(
          "persona",
          new Blob([JSON.stringify(personaData)], { type: "application/json" })
        );

        // Gestión de fotografía
        if (foto) {
          // Subir nueva foto
          formDataUpdate.append("foto", foto);
        } else if (isFotoDeleted) {
          // Eliminar foto existente
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/personas/${persona.id}/foto`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        // Petición PUT para actualizar
        await axios.put(
          `${process.env.REACT_APP_API_URL}/personas/${persona.id}`,
          formDataUpdate,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        // === MODO CREACIÓN (CREATE) ===
        const formDataCreate = new FormData();

        // Añadir datos de persona como JSON
        formDataCreate.append(
          "persona",
          new Blob([JSON.stringify(personaData)], { type: "application/json" })
        );

        // Subir foto si se ha seleccionado
        if (foto) {
          formDataCreate.append("foto", foto);
        }
        // Petición POST para crear
        await axios.post(
          `${process.env.REACT_APP_API_URL}/personas`,
          formDataCreate,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      onSave();
      onClose();
    } catch (err) {
      console.error("Error al guardar persona:", err);
      alert("Error al guardar persona. Revisa consola.");
    }
  };

  /*************************************************************
   *             Renderizado del modal de persona              *
   *************************************************************/
  return (
    /* Contenedor principal (dos modales: formulario + confirmación) */
    <>
    {/* =============================
         Modal principal: formulario
        ============================= */}
    <Modal show={show} onHide={onClose} contentClassName={confirmDeleteFotoModal ? "modal-dimmed" : ""}>
      <Form onSubmit={handleSubmit}>
        {/* Cabecera del modal */}
        <Modal.Header style={{ backgroundColor: "#3b82f6", color: "white" }}>
          <Modal.Title>{persona ? "Editar Trabajador" : "Crear Trabajador"}</Modal.Title>
          <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
        </Modal.Header>

        {/* Cuerpo del modal con campos del formulario */}
        <Modal.Body>
           {/* Campo: Nombre */}
          <Form.Group className="mb-3">
            <Form.Label>Nombre <span style={{color:"red"}}>*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Nombre"
              value={nombre}
              maxLength={50}
              required
              onChange={(e) => setNombre(e.target.value)}
            />
          </Form.Group>

          {/* Campo: Primer Apellido */}
          <Form.Group className="mb-3">
            <Form.Label>Primer Apellido <span style={{color:"red"}}>*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Primer apellido"
              value={apellido1}
              maxLength={50}
              required
              onChange={(e) => setApellido1(e.target.value)}
            />
          </Form.Group>

          {/* Campo: Segundo Apellido */}
          <Form.Group className="mb-3">
            <Form.Label>Segundo Apellido <span style={{color:"red"}}>*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Segundo apellido"
              value={apellido2}
              maxLength={50}
              required
              onChange={(e) => setApellido2(e.target.value)}
            />
          </Form.Group>

          {/* Campo: Cargo */}
          <Form.Group className="mb-3">
            <Form.Label>Cargo</Form.Label>
            <Form.Control
              type="text"
              placeholder="Cargo"
              value={cargo}
              maxLength={250}
              onChange={(e) => setCargo(e.target.value)}
            />
          </Form.Group>

          {/* Campo: Email */}
          <Form.Group className="mb-3">
            <Form.Label>Email <span style={{color:"red"}}>*</span></Form.Label>
            <Form.Control
              type="email"
              placeholder="Email corporativo (@zamora.es)"
              value={email}
              maxLength={50}
              required
              onChange={(e) => setEmail(e.target.value)}
            />
          </Form.Group>

          {/* Campo: Extensión telefónica */}
          <Form.Group className="mb-3">
            <Form.Label>Teléfono</Form.Label>
            <Form.Control
              type="text"
              placeholder="Número de teléfono"
              value={telefono}
              maxLength={9}
              inputMode="numeric"
              pattern="[0-9]*"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setTelefono(value);
              }}
            />
          </Form.Group>

          {/* Campo: Extensión telefónica */}
          <Form.Group className="mb-3">
            <Form.Label>Extensión</Form.Label>
            <Form.Control
              type="text"
              placeholder="Extensión telefónica"
              value={extension}
              maxLength={4}
              inputMode="numeric"
              pattern="[0-9]*"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setExtension(value);
              }}
            />
          </Form.Group>

          {/* Campo: Selección de departamento */}
          <Form.Group className="mb-3">
            <Form.Label>Departamento</Form.Label>
            <Form.Select
              value={departamentoId}
              onChange={(e) => setDepartamentoId(e.target.value)}
            >
              <option value="">Selecciona un departamento</option>
              {departamentos.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nombre}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Campo: Selección de departamento */}
          <Form.Group className="mb-3">
            <Form.Label>Edificio</Form.Label>
            <Form.Select
              value={edificioId}
              onChange={(e) => setEdificioId(e.target.value)}
            >
              <option value="">Selecciona un edificio</option>
              {edificios.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nombre}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* ====== Sección: Gestión de fotografía ====== */}
          <Form.Group className="mb-3">
            <Form.Label>Foto (opcional)</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={(e) => setFoto(e.target.files[0])}
            />

            
            {foto ? (
              // Vista previa de nueva foto
              <div className="mt-2 text-center">
                <div>
                  <img
                    src={URL.createObjectURL(foto)}
                    style={{ width: "100%", maxWidth: "200px", borderRadius: "8px" }}
                    alt="preview"
                  />
                </div>
                <div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      setFoto(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    Quitar foto
                  </Button>
                </div>
              </div>
            ) : fotoActual ? (
              // Vista de foto existente
              <div className="mt-2 text-center">
                <div>
                  <img
                    src={`${process.env.REACT_APP_URL}${fotoActual}`}
                    style={{ width: "100%", maxWidth: "200px", borderRadius: "8px" }}
                    alt="foto"
                  />
                </div>
                <div>
                  <Button
                    variant="danger"
                    size="sm"
                    className="mt-2"
                    onClick={handleConfirmDeleteFoto}
                  >
                    Eliminar foto actual
                  </Button>
                </div>
              </div>
            ) : null}
          </Form.Group>
        </Modal.Body>
        
        {/* Pie del modal con botones de acción */}
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit">{persona ? "Guardar cambios" : "Crear"}</Button>
        </Modal.Footer>
      </Form>
      
    </Modal>
    
    {/* ================================================
          Modal secundario: confirmación eliminar foto
        ================================================ */}
      <Modal
        show={confirmDeleteFotoModal}
        backdrop="static"
        keyboard={false}
        centered
        onHide={() => setConfirmDeleteFotoModal(false)}
      >
        <Modal.Header className="bg-danger text-white">
          <Modal.Title>Eliminar foto</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          ¿Deseas eliminar la foto actual?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDeleteFotoModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDeleteFotoNueva}>
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ModalPersona;


